import {
  Alert,
  AlertTitle,
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import TimeTrackingBotHelp from 'components/@home/drawers/TimeTrackingDrawer/TimeTrackingBotHelp';
import DrawerProvider from 'components/common/Drawer/DrawerProvider';
import Loading from 'components/common/Loading';
import ControlLabel from 'components/common/ControlLabel/ControlLabel';
import Autosuggest from 'components/controls/Autosuggest';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { DEFAULT_CONFIG } from 'services/api/TimeTrackingCompliance';
import Team from 'services/api/Team';
import { TIME_TRACKING_APP_ID, TIME_TRACKING_USER_ID } from 'services/constants';
import appActions from 'store/app/entities/apps/action';
import { useGetByUserIdQuery } from 'store/app/entities/EmployeesApiSlice';
import { useLazyListTeamsQuery } from 'store/app/entities/TeamsSlice';
import { useGetQuery, useUpsertMutation } from 'store/app/entities/AppDataSlice';
import timeTrackingActions from 'store/app/ui/drawers/timeTracking/action';
import authCompany from 'store/selectors/authCompany';
import { I18n } from 'utils/i18n';
import loadOptionsLazyQuery from 'utils/loadOptionsLazyQuery';
import useActions from 'utils/useActions';

const TimeTrackingDrawer = () => {
  const visible = useSelector(state => state.drawers.timeTracking.isOpen);
  const company = useSelector(authCompany);
  const app = useSelector(state => state.apps.apps?.[TIME_TRACKING_APP_ID]);
  const hide = useActions(timeTrackingActions.close);
  const [addEmployee, removeEmployee] = useActions(
    [appActions.addEmployee, appActions.removeEmployee],
    [app],
  );
  const { data: botEmployee } = useGetByUserIdQuery(TIME_TRACKING_USER_ID, {
    skip: !visible,
  });
  const [triggerListTeamsQuery] = useLazyListTeamsQuery();
  const {
    data: configData,
    isFetching: isFetchingConfig,
    isLoading: isLoadingConfig,
    isError: isConfigError,
    error: configError,
  } = useGetQuery({ appId: TIME_TRACKING_APP_ID }, { skip: !visible });
  const [upsertConfig] = useUpsertMutation();

  const methods = useForm({
    defaultValues: DEFAULT_CONFIG,
  });

  const [feedback, setFeedback] = useState(null);
  const initialEnabledRef = useRef(false);

  const hydrateTeams = useCallback(async teams => {
    if (!Array.isArray(teams) || teams.length === 0) {
      return [];
    }
    if (typeof teams[0] === 'object') {
      return teams.map(team => ({
        _id: team?._id ?? team?.value?._id,
        name:
          team?.name ?? team?.value?.name ?? team?.label ?? (typeof team === 'string' ? team : ''),
        role: team?.role ?? team?.value?.role,
      }));
    }

    const hydrated = await Promise.all(
      teams.map(async teamId => {
        try {
          const team = await Team.find(teamId);
          return {
            _id: team._id,
            name: team.description ? `${team.name} - ${team.description}` : team.name,
            role: team.defaultRole,
          };
        } catch (error) {
          console.error('Failed to load team', teamId, error);
          return null;
        }
      }),
    );

    return hydrated.filter(Boolean);
  }, []);

  useEffect(() => {
    if (!visible || !company?._id || isFetchingConfig || isLoadingConfig) {
      return () => {};
    }

    let cancelled = false;
    const loadConfig = async () => {
      const status =
        configError && typeof configError === 'object' && 'status' in configError
          ? configError.status
          : undefined;

      if (isConfigError && status !== 404) {
        setFeedback({
          severity: 'error',
          message: I18n.t('TimeTrackingDrawer.feedback.loadError'),
        });
      }

      const rawConfig =
        configData && !isConfigError
          ? { ...DEFAULT_CONFIG, ...configData }
          : {
              ...DEFAULT_CONFIG,
              enabled: !!botEmployee?.isActive,
            };

      const enabled =
        typeof rawConfig.enabled === 'boolean' ? rawConfig.enabled : !!botEmployee?.isActive;

      const normalizedTeams = await hydrateTeams(rawConfig.teams);

      if (!cancelled) {
        methods.reset({
          ...rawConfig,
          enabled,
          teams: normalizedTeams,
        });
        initialEnabledRef.current = enabled;
      }
    };

    loadConfig();

    return () => {
      cancelled = true;
    };
  }, [
    botEmployee?.isActive,
    company?._id,
    configData,
    configError,
    hydrateTeams,
    isConfigError,
    isFetchingConfig,
    isLoadingConfig,
    methods,
    visible,
  ]);

  useEffect(() => {
    if (!visible) {
      setFeedback(null);
    }
  }, [visible]);

  const handleSubmit = useCallback(
    async values => {
      if (!company?._id) {
        return;
      }
      try {
        setFeedback(null);
        const payload = {
          ...values,
          teams: values.teams?.map(team => team?._id || team).filter(Boolean),
        };
        await upsertConfig({
          appId: TIME_TRACKING_APP_ID,
          data: payload,
        }).unwrap();

        if (values.enabled !== initialEnabledRef.current) {
          await (values.enabled
            ? addEmployee(TIME_TRACKING_APP_ID)
            : removeEmployee(TIME_TRACKING_APP_ID));
          initialEnabledRef.current = values.enabled;
        }

        setFeedback({
          severity: 'success',
          message: I18n.t('TimeTrackingDrawer.feedback.saved'),
        });
      } catch (error) {
        console.error('Failed to save time tracking config', error);
        setFeedback({
          severity: 'error',
          message: I18n.t('TimeTrackingDrawer.feedback.saveError'),
        });
        throw error;
      }
    },
    [addEmployee, company?._id, removeEmployee, upsertConfig],
  );

  const enabled = methods.watch('enabled');
  const complianceMode = methods.watch('complianceMode');
  const autoSendViaOmmnio = methods.watch('autoSendViaOmmnio');

  const pdfDays = useMemo(() => Array.from({ length: 28 }, (_, index) => index + 1), []);

  const drawerContent = (
    <FormProvider {...methods}>
      {isLoadingConfig || isFetchingConfig ? (
        <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
          <Loading size={48} />
        </Box>
      ) : (
        <Stack spacing={4}>
          <TimeTrackingBotHelp />

          {feedback && (
            <Alert
              severity={feedback.severity}
              onClose={() => setFeedback(null)}
              variant="outlined"
            >
              <AlertTitle>
                {feedback.severity === 'success'
                  ? I18n.t('TimeTrackingDrawer.feedback.successTitle')
                  : I18n.t('TimeTrackingDrawer.feedback.errorTitle')}
              </AlertTitle>
              {feedback.message}
            </Alert>
          )}

          <Controller
            name="enabled"
            control={methods.control}
            render={({ field: { value, onChange } }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={value}
                    onChange={(event, checked) => onChange(checked)}
                    color="primary"
                  />
                }
                label={I18n.t('TimeTrackingDrawer.Active')}
              />
            )}
          />

          {enabled && (
            <Controller
              name="complianceMode"
              control={methods.control}
              render={({ field: { value, onChange } }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={value}
                      onChange={(event, checked) => onChange(checked)}
                      color="primary"
                    />
                  }
                  label={I18n.t('TimeTrackingDrawer.fields.complianceMode')}
                />
              )}
            />
          )}

          {enabled && complianceMode && (
            <Stack spacing={3}>
              <Controller
                name="pdfGenerationDay"
                control={methods.control}
                render={({ field: { value, onChange } }) => (
                  <FormControl fullWidth>
                    <InputLabel id="pdfGenerationDay-label">
                      {I18n.t('TimeTrackingDrawer.fields.pdfGenerationDay')}
                    </InputLabel>
                    <Select
                      labelId="pdfGenerationDay-label"
                      label={I18n.t('TimeTrackingDrawer.fields.pdfGenerationDay')}
                      value={value}
                      onChange={event => onChange(Number(event.target.value))}
                    >
                      {pdfDays.map(day => (
                        <MenuItem key={day} value={day}>
                          {I18n.t('TimeTrackingDrawer.fields.pdfGenerationDayOption', { day })}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {I18n.t('TimeTrackingDrawer.helpers.pdfGenerationDay')}
                    </FormHelperText>
                  </FormControl>
                )}
              />

              <Controller
                name="autoSendViaOmmnio"
                control={methods.control}
                render={({ field: { value, onChange } }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={value}
                        onChange={(event, checked) => onChange(checked)}
                        color="primary"
                      />
                    }
                    label={I18n.t('TimeTrackingDrawer.fields.autoSendViaOmmnio')}
                  />
                )}
              />

              {autoSendViaOmmnio && (
                <Controller
                  name="messageType"
                  control={methods.control}
                  render={({ field: { value, onChange } }) => (
                    <FormControl fullWidth>
                      <InputLabel id="messageType-label">
                        {I18n.t('TimeTrackingDrawer.fields.messageType')}
                      </InputLabel>
                      <Select
                        labelId="messageType-label"
                        label={I18n.t('TimeTrackingDrawer.fields.messageType')}
                        value={value}
                        onChange={event => onChange(event.target.value)}
                      >
                        <MenuItem value="normal">
                          {I18n.t('TimeTrackingDrawer.fields.messageTypeNormal')}
                        </MenuItem>
                        <MenuItem value="acknowledge">
                          {I18n.t('TimeTrackingDrawer.fields.messageTypeAcknowledge')}
                        </MenuItem>
                        <MenuItem value="signature">
                          {I18n.t('TimeTrackingDrawer.fields.messageTypeSignature')}
                        </MenuItem>
                      </Select>
                      <FormHelperText component="div">
                        <Box component="ul" sx={{ pl: 3, m: 0 }}>
                          <li>{I18n.t('TimeTrackingDrawer.helpers.messageType.normal')}</li>
                          <li>{I18n.t('TimeTrackingDrawer.helpers.messageType.acknowledge')}</li>
                          <li>{I18n.t('TimeTrackingDrawer.helpers.messageType.signature')}</li>
                        </Box>
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              )}

              <Controller
                name="retentionYears"
                control={methods.control}
                render={({ field: { value, onChange } }) => (
                  <FormControl fullWidth>
                    <InputLabel id="retentionYears-label">
                      {I18n.t('TimeTrackingDrawer.fields.retentionYears')}
                    </InputLabel>
                    <Select
                      labelId="retentionYears-label"
                      label={I18n.t('TimeTrackingDrawer.fields.retentionYears')}
                      value={value}
                      onChange={event => onChange(Number(event.target.value))}
                    >
                      {[4, 5, 6, 7].map(years => (
                        <MenuItem key={years} value={years}>
                          {I18n.t('TimeTrackingDrawer.fields.retentionYearsOption', { years })}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {I18n.t('TimeTrackingDrawer.helpers.retentionYears')}
                    </FormHelperText>
                  </FormControl>
                )}
              />

              <Controller
                name="teams"
                control={methods.control}
                render={({ field: { value, onChange, name } }) => (
                  <ControlLabel
                    name={name}
                    labelPrefix="TimeTrackingDrawer.fields"
                    control={Autosuggest}
                    isMulti
                    isClearable
                    value={value}
                    onChange={selected =>
                      onChange(selected.map(({ value: team }) => team).filter(Boolean))
                    }
                    loadOptions={loadOptionsLazyQuery}
                    additional={{
                      page: 1,
                      trigger: triggerListTeamsQuery,
                      perPage: 50,
                    }}
                    helperText={I18n.t('TimeTrackingDrawer.helpers.teams')}
                  />
                )}
              />

              <Alert severity="info" variant="outlined">
                <AlertTitle>{I18n.t('TimeTrackingDrawer.info.title')}</AlertTitle>
                <Typography variant="body2">
                  {I18n.t('TimeTrackingDrawer.info.description')}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <a
                    href="https://www.congreso.es/public_oficiales/L15/CONG/BOCG/A/BOCG-15-A-58-1.PDF"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {I18n.t('TimeTrackingDrawer.info.link')}
                  </a>
                </Typography>
              </Alert>
            </Stack>
          )}
        </Stack>
      )}
    </FormProvider>
  );

  return (
    <DrawerProvider
      name="TimeTrackingDrawer"
      visible={visible}
      onClose={hide}
      title={app?.name}
      onSubmit={methods.handleSubmit(handleSubmit)}
      sx={{ width: 1000, maxWidth: '95vw' }}
      drawerContent={drawerContent}
    />
  );
};

export default TimeTrackingDrawer;
